"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { getReleaseNotes, type ReleaseNotesPayload } from "@/lib/api/api-client";
import { setAppVersion } from "@/lib/analytics";
import {
  WhatsNewModal,
  type WhatsNewTab,
} from "@/components/ui/organisms/whats-new-modal";

/** localStorage key storing the last release id the user has seen (not a boolean). */
const SEEN_KEY = "virtuosa.whatsnew.seen";

interface WhatsNewContextValue {
  /** Ouvre la popin, sur « Nouveautés » sauf demande contraire. */
  openModal: (tab?: WhatsNewTab) => void;
  /** True when a never-seen release is available — drives the header dot. */
  hasUnread: boolean;
}

const WhatsNewContext = createContext<WhatsNewContextValue | null>(null);

function readSeen(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

export function WhatsNewProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [data, setData] = useState<ReleaseNotesPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [seen, setSeen] = useState<string | null>(null);
  // Onglet demandé par le dernier déclencheur. Volontairement non remis à zéro
  // à la fermeture : la popin s'anime en sortant, et rebasculer d'onglet
  // pendant cette animation se verrait.
  const [requestedTab, setRequestedTab] = useState<WhatsNewTab>("news");

  useEffect(() => {
    setSeen(readSeen());
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      const payload = await getReleaseNotes();
      setData(payload);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const currentReleaseId = data?.currentReleaseId ?? null;
  const hasUnread = currentReleaseId !== null && currentReleaseId !== seen;

  // Ce contexte est le seul à connaître la release courante : il la pousse à
  // l'analytique, qui l'attache à chaque lot. C'est ce qui permet de dire
  // « cette régression est apparue avec telle version ».
  useEffect(() => {
    setAppVersion(currentReleaseId);
  }, [currentReleaseId]);

  const persistSeen = useCallback(() => {
    if (currentReleaseId === null) {
      return;
    }

    try {
      window.localStorage.setItem(SEEN_KEY, currentReleaseId);
    } catch {
      // Ignore storage failures (private mode, quota) — worst case the
      // pastille reste allumée à la prochaine visite, ce qui est sans gravité.
    }

    setSeen(currentReleaseId);
  }, [currentReleaseId]);

  const openModal = useCallback((tab: WhatsNewTab = "news") => {
    setRequestedTab(tab);
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
    persistSeen();
  }, [persistSeen]);

  // Pas d'ouverture automatique : la popin ne s'ouvre que sur action de
  // l'utilisateur. La pastille de l'en-tête (hasUnread) signale la nouveauté.

  // Mémoïsée : le fournisseur enveloppe toute l'application, et un objet neuf
  // à chaque rendu re-rendait chaque consommateur du contexte.
  const value = useMemo(() => ({ openModal, hasUnread }), [openModal, hasUnread]);

  return (
    <WhatsNewContext.Provider value={value}>
      {children}
      <WhatsNewModal
        open={open}
        onClose={closeModal}
        data={data}
        loading={loading}
        error={error}
        onRetry={load}
        defaultEmail={user?.email ?? null}
        pathname={pathname}
        appVersion={currentReleaseId}
        initialTab={requestedTab}
      />
    </WhatsNewContext.Provider>
  );
}

export function useWhatsNew(): WhatsNewContextValue {
  const ctx = useContext(WhatsNewContext);

  if (ctx === null) {
    throw new Error("useWhatsNew must be used within a WhatsNewProvider");
  }

  return ctx;
}
